/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';
import { useCallback, useEffect } from 'react';

import type { CKEditor5PreviewHandlerProps } from '../../components/CKEditor5Preview/types';
import type { EditorLanguage } from '../../components/LanguageSelection/languages';
import type { EditorPlugins } from '../../types/EditorPlugins';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';

type UseEventListenerToSynchronizeEditorProps = {
	editorWidth: number;
	editorHeight: number;
	locationOrigin: string;
	isFullscreen: boolean;
	onEditorTypeChange: ( editorType: EditorType ) => void;
	onEditorPluginsChange: ( editorPlugins: EditorPlugins ) => void;
	onEditorLanguageChange: ( editorLanguage: EditorLanguage ) => void;
	onToolbarOptionsChange: ( toolbarOptions: ToolbarOptions ) => void;
	onWindowHeightChange: ( windowHeight: number ) => void;
	onPresetChange: ( editorPreset: string ) => void;
};

export function useIframeToAppSynchronization( {
	editorWidth,
	editorHeight,
	locationOrigin,
	isFullscreen,
	onEditorTypeChange,
	onEditorPluginsChange,
	onEditorLanguageChange,
	onToolbarOptionsChange,
	onWindowHeightChange,
	onPresetChange
}: UseEventListenerToSynchronizeEditorProps ): void {
	const synchronizationHandler = useCallback( ( evt: CKEditor5PreviewHandlerProps ) => {
		if ( evt.origin !== locationOrigin ) {
			return;
		}

		if ( !evt.data.isCKEditor5BuilderEvent ) {
			return;
		}

		if ( evt.data.eventType === 'windowHeightChange' ) {
			const { windowHeight } = evt.data;

			onWindowHeightChange( windowHeight );
		}
		else if ( evt.data.eventType === 'editorDataChange' ) {
			const {
				editorType,
				editorLanguage,
				editorPlugins,
				toolbarOptions,
				presetName
			} = evt.data;

			onEditorTypeChange( editorType );
			onEditorLanguageChange( editorLanguage );
			onEditorPluginsChange( editorPlugins );
			onToolbarOptionsChange( toolbarOptions );
			onPresetChange( presetName );
		}
	}, [
		locationOrigin,
		onWindowHeightChange,
		onEditorLanguageChange,
		onEditorPluginsChange,
		onEditorTypeChange,
		onToolbarOptionsChange,
		onPresetChange
	] );

	useEffect( () => {
		const eventListenerArgs = [ 'message', synchronizationHandler, false ] as const;

		window.addEventListener( ...eventListenerArgs );

		return () => window.removeEventListener( ...eventListenerArgs );
	}, [ synchronizationHandler ] );

	useEffect( () => {
		window.parent.postMessage( { editorWidth, editorHeight, isCKEditor5BuilderEvent: true }, locationOrigin );
	}, [ editorWidth, editorHeight, locationOrigin ] );

	useEffect( () => {
		window.parent.postMessage( { isFullscreen, isFullscreenEvent: true }, locationOrigin );
	}, [ isFullscreen, locationOrigin ] );
}
